import React, { useEffect, useState, useRef, RefObject } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons';

import { Paper, Button } from '../../Common/Components';
import { PasswordStrengthIndicator } from '../../Common/PasswordStrengthIndicator';
import CheckboxInput from '../../Common/CheckboxInput';
import { config } from '../../../configs/config';
import { useSnackbar } from '../../Context/SnackbarContext';

export interface MerchantSetPasswordProps {
  title: string;
  onSetPassword: (password: string) => void;
  passwordStrength: (password: string) => { score: number };
  buttonText: string;
  showTerms?: boolean;
  rememberMeRef?: RefObject<HTMLInputElement>;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',

    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },
  inputWrapper: {
    width: '100%',
    marginBottom: '30px',
  },
  input: {
    color: theme.palette.grey[600],
    borderRadius: '16px',
    borderColor: theme.palette.grey[600],
    borderWidth: '1px',

    '& input:-internal-autofill-selected,input:-webkit-autofill': {
      boxShadow: `0 0 0px 50px ${theme.palette.background.paper} inset`,
    },

    '&:hover + fieldset': {
      borderColor: theme.palette.grey[600],
    },
  },
  inputLabel: {
    color: theme.palette.grey[600],
  },
  header: {
    display: 'grid',
    gridGap: '16px',
    marginBottom: '40px',
  },
  checkboxWrapper: {
    marginBottom: '24px',
  },
}));

const MerchantSetPasswordView: React.FC<MerchantSetPasswordProps> = ({
  title,
  onSetPassword,
  passwordStrength,
  rememberMeRef,
  showTerms,
  buttonText,
}) => {
  const classes = useStyles();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [score, setScore] = useState(-1);
  const [passwordMatch, setPasswordMatch] = useState<boolean | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const legalRef = useRef<HTMLInputElement>(null);
  const { openSnackbar } = useSnackbar();

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowPasswordConfirm = () => setShowPasswordConfirm(!showPasswordConfirm);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showTerms && !legalRef?.current?.checked) {
      openSnackbar('Please accept our terms and conditions before continuing.');
      return;
    }

    if (passwordMatch && score > 2) {
      onSetPassword(password);
    }
  };

  useEffect(() => { 
    const timer = setTimeout(async () => {
      if (!password) return;

      const strengthScore = passwordStrength(password).score;
      setScore(strengthScore);
      setPasswordMatch(passwordConfirm ? password === passwordConfirm : undefined);
    }, 1000);

    return () => clearTimeout(timer);
  }, [password, passwordConfirm, passwordStrength]);

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (!e.target.value) setScore(-1);
  };

  return (
    <Paper className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h1" color="primary">
          {title}
        </Typography>
      </div>

      <form onSubmit={onSubmit}>
        <TextField
          id="password"
          name="password"
          label="Enter Password"
          variant="outlined"
          className={classes.inputWrapper}
          focused={false}
          autoComplete="new-password"
          type={showPassword ? 'text' : 'password'}
          InputLabelProps={{ className: classes.inputLabel }}
          value={password}
          onChange={onPasswordChange}
          InputProps={{
            className: classes.input,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <VisibilityOutlined color="primary" />
                  ) : (
                    <VisibilityOffOutlined color="primary" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {score >= 0 && !!password && (
          <PasswordStrengthIndicator strong={score > 2} passwordMatch={passwordMatch} />
        )}

        <TextField
          id="passwordConfirm"
          name="passwordConfirm"
          label="Confirm Password"
          variant="outlined"
          className={classes.inputWrapper}
          focused={false}
          autoComplete="new-password"
          type={showPasswordConfirm ? 'text' : 'password'}
          InputLabelProps={{ className: classes.inputLabel }}
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          InputProps={{
            className: classes.input,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPasswordConfirm}
                  tabIndex={-1}
                >
                  {showPasswordConfirm ? (
                    <VisibilityOutlined color="primary" />
                  ) : (
                    <VisibilityOffOutlined color="primary" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {showTerms && (
          <CheckboxInput id="termsAgreed" ref={legalRef}>
            <Typography color="textPrimary">
              <a href={config.termsOfService} target="_blank" rel="noopener noreferrer">
                Terms & Conditions,{' '}
              </a>
              <a href={config.privacyPolicy} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href={config.merchantEula} target="_blank" rel="noopener noreferrer">
                End User License Agreement
              </a>
            </Typography>
          </CheckboxInput>
        )}

        {rememberMeRef && (
          <div className={classes.checkboxWrapper}>
            <CheckboxInput id="rememberMe" ref={rememberMeRef}>
              Remember Me
            </CheckboxInput>
          </div>
        )}

        <Button className="wide" type="submit">
          {buttonText}
        </Button>
      </form>
    </Paper>
  );
};

export default MerchantSetPasswordView;
